// TOTP 2FA gate for the Super Admin panel.
// First-time setup: fetches /totp/setup, renders QR + manual secret, user
// scans with Google/Microsoft Authenticator and enters the 6-digit code.
// Subsequent logins: single 6-digit input against /totp/login.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function TotpGate({ status, onSuccess }) {
  const isSetup = !status.totp_enabled;
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSetup) return;
    (async () => {
      try {
        const { data } = await api.post("/super-admin/totp/setup");
        setSetupData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Kon 2FA-setup niet laden");
      }
    })();
  }, [isSetup]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = isSetup ? "/super-admin/totp/enable" : "/super-admin/totp/login";
      const { data } = await api.post(path, { code: code.trim() });
      if (data.token) localStorage.setItem("bh_sa_token", data.token);
      toast.success(isSetup ? "2FA ingesteld" : "Welkom terug");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code klopt niet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f8f6] px-6 py-16">
      <div className="w-full max-w-md bg-white rounded-[var(--global-radius-xl)] border border-gray-200 p-8 shadow-sm">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-3">Super Admin</div>
        <h1 className="font-display text-2xl font-medium text-[#1a1a1a] mb-2">
          {isSetup ? "Stel 2FA in" : "Vul je 2FA-code in"}
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          {isSetup
            ? "Scan de QR-code met Google Authenticator (of Microsoft Authenticator / 1Password) en voer de 6-cijferige code in."
            : "Open je authenticator-app en voer de huidige 6-cijferige code in."}
        </p>

        {isSetup && setupData && (
          <div className="mb-6 rounded-[var(--global-radius-lg)] border border-gray-200 bg-[#f8f8f6] p-4">
            <div
              className="w-full flex items-center justify-center bg-white rounded-[var(--global-radius)] p-3 mb-3 [&_svg]:w-full [&_svg]:max-w-[200px] [&_svg]:h-auto"
              dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
              data-testid="totp-qr"
            />
            <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">Handmatige code</div>
            <div className="font-mono text-xs text-[#1a1a1a] break-all select-all" data-testid="totp-secret">{setupData.secret}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-400 mb-1 block">
              6-cijferige code
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              placeholder="123456"
              className="h-12 text-center font-mono text-xl tracking-[0.4em] bg-white"
              autoFocus
              data-testid="totp-code"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || code.length !== 6}
            className="w-full h-11 rounded-[var(--global-radius)] bg-[var(--global-action)] hover:opacity-90 text-white"
            data-testid="totp-submit"
          >
            {busy ? "Bezig…" : (isSetup ? "2FA activeren" : "Verifieer")}
          </Button>
        </form>
      </div>
    </div>
  );
}
